/**
 * `useSourceExtraction` — read the `.processed.md` MACHINE-EXTRACTION
 * companion that the ingest chain writes next to every frozen
 * `sources/*` original, so a viewer can show WHAT WAS EXTRACTED from a
 * file the browser cannot render (`.xlsx`, `.pptx`, `.msg`, …) instead
 * of a dead "no preview available" message.
 *
 * **The data path** is the one `useSourceInfoFile` already proved: the
 * runtime's `case.workspace_tree` walk deliberately HIDES the machine
 * companions (`isInternalFile` in `lexiun-core/gateway-methods/case-tree.ts`
 * drops `*.processed.md` + `*.info.md`), but `document.read_bytes` reaches
 * hidden paths just fine. So we compute the parallel path and read it
 * directly — no new runtime endpoint, no change to what the tree shows.
 *
 * Contract of the file we are reading (mission-ingest-implementation-plan.md
 * §5 / §13.1(a)): flat YAML frontmatter, then the extraction as markdown.
 *
 *   ---
 *   source_id: fc47bd32…
 *   original_name: "ACORDOS Buffet-Rizomar.xlsx"
 *   processed_name: controle-aluguel-…__documento__20260731-044654.xlsx
 *   modality: office
 *   doctype: documento
 *   parties: [Buffet, Fantastikday, Rizomar]
 *   extraction: { method: office2md, confidence: 0.9, egress: llm, status: ok }
 *   ---
 *   # <h1 the intake harness derived>
 *   …the sheets / pages / transcript as markdown…
 *
 * ## Why the result has FOUR states and not a boolean
 *
 * "I could not look" is not "there is nothing there" (the
 * unknown-must-be-representable rule). A source that legitimately has no
 * companion — a legacy source predating the ingest contract — must render
 * differently from one whose companion we failed to READ, because the
 * first is a normal end state and the second means the lawyer is being
 * shown less than exists. `NOT_FOUND` from the gateway is the
 * discriminator: `GatewayRpcError.code` carries it verbatim.
 *
 * And `present` is NOT the same as usable: an intake that failed still
 * writes a companion, with `extraction.status` recording the failure. A
 * caller that renders a failed extraction as if it were content is
 * showing the lawyer a stub and calling it their document — so the
 * parsed `extraction.status` rides on the result and callers are
 * expected to branch on it.
 */

import { useEffect, useState } from "react";
import { useFileBytes } from "./useFileBytes";
import { GatewayRpcError } from "@/lib/gateway-client";
import { basename, type FileRef } from "@/stores/useWorkspaceFileStore";

/** Parsed shape of the `extraction: { … }` inline map. */
export interface ExtractionStamp {
  method?: string;
  /** 0..1 as written by the harness. `undefined` when unparseable —
   *  never silently 0, which would read as "no confidence at all". */
  confidence?: number;
  egress?: string;
  /** `ok` on success; the intake failure classifier writes other values
   *  (e.g. `extract_failed`). Absent on companions that predate the stamp. */
  status?: string;
}

export interface SourceExtraction {
  /** Frontmatter-stripped markdown body — what the lawyer should read. */
  body: string;
  /** Flat frontmatter, verbatim. Values that LOOK like inline arrays or
   *  maps are kept as their raw string; the typed accessors below carry
   *  the parsed forms. */
  frontmatter: Record<string, string>;
  /** The uploader's OWN filename, e.g. "ACORDOS Buffet-Rizomar.xlsx".
   *  The single most useful field here: the frozen original is renamed to
   *  a generated slug at ingest, so this is the only string that connects
   *  what the lawyer sees to what the lawyer uploaded. */
  originalName?: string;
  /** First `# heading` the intake harness derived, when present. */
  h1?: string;
  doctype?: string;
  modality?: string;
  parties: string[];
  extraction: ExtractionStamp;
}

export type SourceExtractionState =
  | "loading"
  /** Not an eligible source at all (outside `sources/`, or itself a companion). */
  | "ineligible"
  /** Read returned NOT_FOUND — this source genuinely has no companion. */
  | "absent"
  /** We failed to read or parse it. NOT the same as `absent`. */
  | "unreadable"
  | "present";

export interface UseSourceExtractionResult {
  state: SourceExtractionState;
  /** Non-null only when `state === "present"`. */
  extraction: SourceExtraction | null;
  error: Error | null;
}

/**
 * Compute the sibling `.processed.md` path for a frozen original.
 * Returns null when the file is not an eligible source — mirrors
 * `infoPathForSource` exactly, including the "sidecars don't have
 * sidecars" rule, so the two companions are addressed the same way.
 */
export function extractionPathForSource(file: FileRef): string | null {
  if (!file.path.startsWith("sources/")) return null;
  const name = basename(file.path);
  if (name.endsWith(".info.md") || name.endsWith(".processed.md")) return null;
  const dot = name.lastIndexOf(".");
  const stem = dot < 0 ? name : name.slice(0, dot);
  // Preserve any subzone (`sources/email/…`) — the companion is written
  // NEXT TO its original, not flattened back up to `sources/`.
  const dir = file.path.slice(0, file.path.length - name.length);
  return `${dir}${stem}.processed.md`;
}

/** Strip a leading YAML frontmatter block, returning the body. */
function stripFrontmatter(raw: string): string {
  return raw.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, "").trim();
}

/**
 * Parse an inline YAML map — `{ method: office2md, confidence: 0.9 }`.
 * Deliberately tiny: the ingest contract writes flat scalar pairs only.
 * Unparseable values are simply omitted rather than coerced, so a caller
 * can tell "not stated" from a real value.
 */
function parseInlineMap(value: string): Record<string, string> {
  const inner = value.trim().replace(/^\{/, "").replace(/\}$/, "");
  const out: Record<string, string> = {};
  for (const pair of inner.split(",")) {
    const colon = pair.indexOf(":");
    if (colon < 0) continue;
    const k = pair.slice(0, colon).trim();
    const v = pair.slice(colon + 1).trim();
    if (k) out[k] = v;
  }
  return out;
}

/** Parse an inline YAML sequence — `[Buffet, Fantastikday]`. */
function parseInlineList(value: string): string[] {
  const inner = value.trim().replace(/^\[/, "").replace(/\]$/, "");
  return inner
    .split(",")
    .map((s) => s.trim().replace(/^["']|["']$/g, ""))
    .filter(Boolean);
}

/** Unwrap the quoting the harness uses for values containing spaces. */
function unquote(value: string): string {
  return value.replace(/^["']|["']$/g, "");
}

/**
 * Parse a `.processed.md` into {@link SourceExtraction}.
 *
 * Exported for the colocated unit test: the frontmatter grammar is the
 * one thing here that a future ingest change could silently break, so it
 * is asserted against real captured fixtures rather than only through
 * the hook.
 */
export function parseProcessedMd(raw: string): SourceExtraction {
  const frontmatter: Record<string, string> = {};
  const fm = raw.match(/^---\r?\n([\s\S]*?)\r?\n---/);
  if (fm) {
    for (const rawLine of fm[1].split(/\r?\n/)) {
      const line = rawLine.trim();
      if (!line || line.startsWith("#")) continue;
      const colon = line.indexOf(":");
      if (colon <= 0) continue;
      const key = line.slice(0, colon).trim();
      const value = line.slice(colon + 1).trim();
      if (key) frontmatter[key] = value;
    }
  }

  const body = stripFrontmatter(raw);
  const h1 = body.match(/^#\s+(.+)$/m)?.[1]?.trim();

  const stampRaw = frontmatter.extraction ?? "";
  const stamp = stampRaw.startsWith("{") ? parseInlineMap(stampRaw) : {};
  const confidence = Number.parseFloat(stamp.confidence ?? "");

  return {
    body,
    frontmatter,
    originalName: frontmatter.original_name
      ? unquote(frontmatter.original_name)
      : undefined,
    h1,
    doctype: frontmatter.doctype || undefined,
    modality: frontmatter.modality || undefined,
    parties: frontmatter.parties?.startsWith("[")
      ? parseInlineList(frontmatter.parties)
      : [],
    extraction: {
      method: stamp.method,
      // NaN would render as a number; drop it instead so "unknown"
      // stays unknown rather than becoming a confident-looking value.
      confidence: Number.isFinite(confidence) ? confidence : undefined,
      egress: stamp.egress,
      status: stamp.status,
    },
  };
}

/**
 * Did this extraction actually succeed?
 *
 * Three cases, and the middle one is the trap:
 *  - `status: ok`      → a stated success.
 *  - no `status` key   → a companion written before the stamp existed;
 *                        that IS the legacy success shape, and treating
 *                        it as failure would blank out every older source.
 *  - `status:` present but EMPTY, or any other value → NOT a success.
 *    A blank status is a malformed record, not a quiet yes. The asymmetry
 *    is deliberate: showing a warning over real content is recoverable in
 *    a click, while showing a failed stub as though it were the lawyer's
 *    document is not.
 */
export function extractionSucceeded(e: SourceExtraction): boolean {
  return e.extraction.status === undefined || e.extraction.status === "ok";
}

export function useSourceExtraction(
  file: FileRef | null,
): UseSourceExtractionResult {
  const path = file ? extractionPathForSource(file) : null;
  const bytes = useFileBytes(path);
  const [parsed, setParsed] = useState<{
    e: SourceExtraction | null;
    err: Error | null;
  }>({ e: null, err: null });

  useEffect(() => {
    if (!bytes.url) {
      setParsed({ e: null, err: null });
      return;
    }
    let cancelled = false;
    fetch(bytes.url)
      .then((r) => r.text())
      .then((text) => {
        if (cancelled) return;
        try {
          setParsed({ e: parseProcessedMd(text), err: null });
        } catch (err) {
          setParsed({ e: null, err: err as Error });
        }
      })
      .catch((err) => {
        if (!cancelled) setParsed({ e: null, err: err as Error });
      });
    return () => {
      cancelled = true;
    };
  }, [bytes.url]);

  if (!path) {
    return { state: "ineligible", extraction: null, error: null };
  }

  const err = bytes.error ?? parsed.err;
  if (bytes.isError || parsed.err) {
    // The ONLY signal that separates "this source has no companion" from
    // "the read failed" — matched on the gateway's error CODE, never on
    // its (localized) prose.
    const absent =
      bytes.error instanceof GatewayRpcError && bytes.error.code === "NOT_FOUND";
    return {
      state: absent ? "absent" : "unreadable",
      extraction: null,
      error: absent ? null : (err ?? null),
    };
  }
  if (bytes.isPending) {
    // Covers a DISABLED query too (TanStack v5 keeps `status:"pending"`
    // while `enabled` is false), which is correct: gateway not connected
    // yet is a not-answered-yet, not an answer.
    return { state: "loading", extraction: null, error: null };
  }
  if (parsed.e) {
    return { state: "present", extraction: parsed.e, error: null };
  }
  // Settled, no error, nothing parsed. Two different worlds:
  //  - a blob URL exists → the decode/parse effect simply has not run for
  //    it yet. Genuinely still loading.
  //  - no blob URL → the read settled without ever producing bytes. Do
  //    NOT report that as loading: an eternal spinner is how "we failed"
  //    disguises itself as "we are trying".
  return bytes.url
    ? { state: "loading", extraction: null, error: null }
    : { state: "unreadable", extraction: null, error: err ?? null };
}
